import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import LandingPage from "@/pages/LandingPage";
import AboutPage from "@/pages/AboutPage";
import SampleRevealPage from "@/pages/SampleRevealPage";
import SampleReveal from "@/components/SampleReveal";
import SampleRevealRevolut from "@/components/SampleRevealRevolut";
import SampleRevealKlarna from "@/components/SampleRevealKlarna";
import SampleRevealDB from "@/components/SampleRevealDB";
import SampleRevealCiti from "@/components/SampleRevealCiti";
import SignUpPage from "@/pages/SignUpPage";
import SignInPage from "@/pages/SignInPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import IntakeForm from "@/pages/IntakeForm";
import ReIntakePage from "@/pages/ReIntakePage";
import IntelligenceView from "@/pages/IntelligenceView";
import UpgradePage from "@/pages/UpgradePage";
import SettingsPage from "@/pages/SettingsPage";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import AdminAccountsPage from "@/pages/AdminAccountsPage";
import AdminAccountRequestsPage from "@/pages/AdminAccountRequestsPage";
import AdminPreviewPage from "@/pages/AdminPreviewPage";
import AdminEvalPage from "@/pages/AdminEvalPage";
import AdminLoginActivityPage from "@/pages/AdminLoginActivityPage";
import MockCardOnlyPage from "@/pages/MockCardOnlyPage";
import WaitlistPage from "@/pages/WaitlistPage";

// v5.2 / Landing v3 — hide the "Made with Emergent" badge on every route,
// public and authenticated alike. The hide is CSS/inline-style based and
// reversible by removing this controller.
function EmergentBadgeVisibilityController() {
  useEffect(() => {
    const badge = document.getElementById("emergent-badge");
    if (!badge) return;
    badge.style.visibility = "hidden";
    badge.style.pointerEvents = "none";
    badge.setAttribute("hidden", "true");
  }, []);
  return null;
}

// 2026-04-27 — Email-verification gate. Authenticated routes (intake, intake/re,
// intelligence, settings/*, admin/*) refuse navigation when the user is signed
// in but `email_verified` is false. Public routes (/, /sample, /signup, /signin,
// /waitlist) are always accessible. The /verify-email route is the only place
// an unverified user can land.
const PUBLIC_ROUTES = new Set([
  "/", "/about", "/sample", "/sample/lbg", "/sample/revolut", "/sample/klarna", "/sample/db", "/sample/citi", "/signup", "/signin", "/waitlist", "/verify-email",
]);

function EmailVerificationGate() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Loading or signed-out — no gate decision yet.
    if (user === undefined || user === null) return;
    if (user.email_verified) return;
    // Public routes always pass; verify page is the only allowed landing for
    // unverified users.
    if (PUBLIC_ROUTES.has(location.pathname)) return;
    const next = encodeURIComponent(location.pathname + location.search);
    navigate(`/verify-email?next=${next}`, { replace: true });
  }, [user, location.pathname, location.search, navigate]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <EmergentBadgeVisibilityController />
        <EmailVerificationGate />
        <Toaster position="top-right" />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/sample" element={<SampleRevealPage />} />
          <Route path="/sample/lbg" element={<SampleReveal />} />
          <Route path="/sample/revolut" element={<SampleRevealRevolut />} />
          <Route path="/sample/klarna" element={<SampleRevealKlarna />} />
          <Route path="/sample/db" element={<SampleRevealDB />} />
          <Route path="/sample/citi" element={<SampleRevealCiti />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/intake" element={<IntakeForm />} />
          <Route path="/intake/re" element={<ReIntakePage />} />
          <Route path="/intelligence" element={<IntelligenceView />} />
          <Route path="/upgrade" element={<UpgradePage />} />
          <Route path="/settings/profile" element={<SettingsPage />} />
          <Route path="/settings/account" element={<AccountSettingsPage />} />
          <Route path="/admin/accounts" element={<AdminAccountsPage />} />
          <Route path="/admin/account-requests" element={<AdminAccountRequestsPage />} />
          <Route path="/admin/preview" element={<AdminPreviewPage />} />
          <Route path="/admin/preview/mock-card-only" element={<MockCardOnlyPage />} />
          <Route path="/admin/eval" element={<AdminEvalPage />} />
          <Route path="/admin/login-activity" element={<AdminLoginActivityPage />} />
          <Route path="/waitlist" element={<WaitlistPage />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
